import React, { useState } from "react";
import { Link } from "react-router-dom";
import { forgotPassword } from "../api/authApi";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";
import { useToast } from "../context/ToastContext";
import {
  ArrowLeft,
  CheckCircle,
  Loader2,
  Mail,
  Globe,
  Sun,
  Moon,
  Monitor,
} from "lucide-react";
import "./Auth.css";

function ForgotPassword() {
  const { t, language, isRTL, toggleLanguage } = useLanguage();
  const { themePreference, setThemePreference } = useTheme();
  const { addToast } = useToast();

  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await forgotPassword(email);
      setSuccess(true);
      if (addToast) {
        addToast(t("forgotPassword.toastSent"), "success");
      }
    } catch (err) {
      setError(err.message || t("forgotPassword.errFailedDefault"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-page-container">
        {/* Quick Language & Theme Controls */}
        <div className="auth-toolbar">
          {/* Language Switcher */}
          <button
            type="button"
            className="auth-util-btn"
            onClick={toggleLanguage}
            title={language === "ar" ? "Switch to English" : "التبديل إلى العربية"}
            aria-label={language === "ar" ? "Switch to English" : "التبديل إلى العربية"}
          >
            <Globe size={14} />
            <span>{language === "ar" ? "English" : "العربية"}</span>
          </button>

          {/* Theme Segmented Switcher */}
          <div className="auth-theme-group" role="group" aria-label={t("verifyEmail.themeMode")}>
            <button
              type="button"
              className={`auth-theme-option ${themePreference === "light" ? "is-active" : ""}`}
              onClick={() => setThemePreference("light")}
              title={t("verifyEmail.themeLight")}
              aria-label={t("verifyEmail.themeLight")}
              aria-pressed={themePreference === "light"}
            >
              <Sun size={13} />
              <span className="auth-theme-text">{t("verifyEmail.themeLight")}</span>
            </button>
            <button
              type="button"
              className={`auth-theme-option ${themePreference === "dark" ? "is-active" : ""}`}
              onClick={() => setThemePreference("dark")}
              title={t("verifyEmail.themeDark")}
              aria-label={t("verifyEmail.themeDark")}
              aria-pressed={themePreference === "dark"}
            >
              <Moon size={13} />
              <span className="auth-theme-text">{t("verifyEmail.themeDark")}</span>
            </button>
            <button
              type="button"
              className={`auth-theme-option ${themePreference === "system" ? "is-active" : ""}`}
              onClick={() => setThemePreference("system")}
              title={t("verifyEmail.themeSystem")}
              aria-label={t("verifyEmail.themeSystem")}
              aria-pressed={themePreference === "system"}
            >
              <Monitor size={13} />
              <span className="auth-theme-text">{t("verifyEmail.themeSystem")}</span>
            </button>
          </div>
        </div>

        {/* Forgot Password Card */}
        <div
          className="auth-card"
          style={{
            textAlign: success ? "center" : undefined,
            padding: "40px 32px",
          }}
        >
          {success ? (
            <div>
              <div className="auth-status-icon-wrap auth-status-icon--gold">
                <CheckCircle size={48} />
              </div>
              <h2 className="auth-title">{t("forgotPassword.checkEmailTitle")}</h2>
              <p className="auth-subtitle" style={{ maxWidth: "380px", margin: "0 auto 24px" }}>
                {t("forgotPassword.checkEmailSubtitle")}{" "}
                <strong dir="ltr" style={{ display: "inline-block" }}>
                  {email}
                </strong>
                .
              </p>
              <Link
                to="/login"
                className="btn-primary"
                style={{ display: "inline-block", marginTop: "12px" }}
              >
                {t("forgotPassword.returnToSignIn")}
              </Link>
            </div>
          ) : (
            <>
              <div className="auth-header" style={{ textAlign: "center", marginBottom: "24px" }}>
                <h2 className="auth-title">{t("forgotPassword.title")}</h2>
                <p className="auth-subtitle">{t("forgotPassword.subtitle")}</p>
              </div>

              <form className="auth-form" onSubmit={handleSubmit} style={{ textAlign: "start" }}>
                {error && (
                  <div className="auth-error" role="alert">
                    {error}
                  </div>
                )}

                <div className="auth-field">
                  <label htmlFor="email">{t("forgotPassword.emailLabel")}</label>
                  <div className="auth-input-wrapper auth-input-wrapper--ltr">
                    <span className="auth-input-icon">
                      <Mail size={16} />
                    </span>
                    <input
                      type="email"
                      id="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t("forgotPassword.emailPlaceholder")}
                      autoComplete="email"
                      dir="ltr"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn-primary auth-submit"
                  disabled={loading}
                  style={{ width: "100%", justifyContent: "center" }}
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="auth-spinner" />
                      <span style={{ marginInlineStart: "8px" }}>
                        {t("forgotPassword.submitting")}
                      </span>
                    </>
                  ) : (
                    t("forgotPassword.submitBtn")
                  )}
                </button>
              </form>

              <div className="auth-footer" style={{ marginTop: "24px" }}>
                <Link
                  to="/login"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    color: "var(--text-secondary)",
                    fontSize: "13px",
                    textDecoration: "none",
                  }}
                >
                  <ArrowLeft
                    size={14}
                    style={{
                      transform: isRTL ? "rotate(180deg)" : "none",
                      transition: "transform 0.2s ease",
                    }}
                  />
                  <span>{t("forgotPassword.backToSignIn")}</span>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPassword;
